/**
 * Retention admin screen — Stage D of the retention bonus build.
 *
 * Operational visibility into the winback funnel:
 *   • offered → user got the modal but hasn't responded
 *   • accepted → user said "I'll stay," bonus is vesting
 *   • vested → 7 days passed + user still active, bonus credited
 *   • declined → user explicitly chose to stop earning
 *   • expired → user went silent during the vest window, never claimed
 *
 * The headline stats up top tell you whether the mechanic is
 * working at all (offered > 0?) and how well (accept-rate). The
 * table below is for triaging individual stuck offers.
 *
 * Per feedback_points_not_money.md, this isn't a fraud surface —
 * it's just operational observability. The "force status" action
 * exists for edge cases (cron crashed, user reported a bug in the
 * vest logic) not for fraud response.
 */
import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  AppRef,
  PublisherRef,
  RetentionStatus
} from '@/api/retention';
import {
  useRetention,
  useRetentionOne,
  useRetentionStats,
  useUpdateRetention
} from '@/hooks/useRetention';
import {
  ActionButton,
  Badge,
  Card,
  Pagination,
  SectionLabel,
  Select,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDate, fmtDateTime } from '@/lib/format';

const STATUS_LABEL: Record<RetentionStatus, string> = {
  offered: 'Offered',
  accepted: 'Accepted',
  vested: 'Vested',
  expired: 'Expired',
  declined: 'Declined'
};

const STATUS_OPTIONS: Array<{ value: RetentionStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'offered', label: 'Offered (pending)' },
  { value: 'accepted', label: 'Accepted (vesting)' },
  { value: 'vested', label: 'Vested (credited)' },
  { value: 'expired', label: 'Expired (no credit)' },
  { value: 'declined', label: 'Declined' }
];

// ─── Stats overview ───────────────────────────────────────────────────────

function StatsOverview() {
  const { data, isLoading } = useRetentionStats();
  const stats = data?.byStatus || {};
  const acceptPct = data?.acceptRate
    ? `${Math.round(data.acceptRate * 100)}%`
    : '—';
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5 lg:gap-4">
      <StatCard
        label="Offered"
        value={stats.offered || 0}
        color="text-amber-300"
        loading={isLoading}
      />
      <StatCard
        label="Accepted"
        value={(stats.accepted || 0) + (stats.vested || 0)}
        color="text-emerald-300"
        loading={isLoading}
      />
      <StatCard
        label="Declined"
        value={stats.declined || 0}
        loading={isLoading}
      />
      <StatCard
        label="Accept rate"
        value={acceptPct}
        color="text-sky-300"
        loading={isLoading}
      />
      <StatCard
        label="Last 7d"
        value={data?.last7d || 0}
        color="text-amber-200"
        loading={isLoading}
      />
    </div>
  );
}

// ─── Detail modal ─────────────────────────────────────────────────────────

interface DetailModalProps {
  offerId: string;
  onClose: () => void;
}

function DetailModal({ offerId, onClose }: DetailModalProps) {
  const { data: offer, isLoading: isFetching } = useRetentionOne(offerId);
  const update = useUpdateRetention();
  const [internalNotes, setInternalNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (offer) {
      setInternalNotes(offer.internalNotes || '');
    }
  }, [offer]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  if (isFetching || !offer) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        onClick={onClose}
      >
        <div className="w-full max-w-2xl rounded-2xl border border-nurev-border bg-nurev-surface-lift p-6">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="mt-3 h-24 w-full" />
        </div>
      </div>
    );
  }

  const publisher: PublisherRef | null =
    typeof offer.publisherId === 'object' && offer.publisherId !== null
      ? (offer.publisherId as PublisherRef)
      : null;
  const app: AppRef | null =
    typeof offer.appId === 'object' && offer.appId !== null
      ? (offer.appId as AppRef)
      : null;

  const snapshot = offer.eligibilitySnapshot || {};
  const isTerminal =
    offer.status === 'vested' ||
    offer.status === 'expired' ||
    offer.status === 'declined';

  const handleForceStatus = async (status: RetentionStatus) => {
    setError(null);
    try {
      await update.mutateAsync({
        id: offer._id,
        status,
        internalNotes
      });
      onClose();
    } catch (err) {
      setError(extractErr(err));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-nurev-border bg-nurev-surface-lift p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-nurev-text">
              Retention offer · {offer.bonusAmount.toLocaleString()}{' '}
              {app?.currencyName || 'points'}
            </h3>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge value={STATUS_LABEL[offer.status]} />
              {publisher && (
                <span className="rounded bg-white/[0.06] px-2 py-0.5 text-[10px] font-medium text-nurev-text-soft">
                  {publisher.companyName || publisher.name}
                </span>
              )}
              {app && (
                <span className="rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-300 ring-1 ring-amber-500/30">
                  {app.name || app.appKey}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-nurev-muted hover:bg-white/5 hover:text-nurev-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Timeline — most-useful single piece of info on the screen */}
        <Card className="mb-4 bg-nurev-surface">
          <SectionLabel>Timeline</SectionLabel>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <KV label="Person ID">
              <code className="text-[11px]">{offer.personId}</code>
            </KV>
            <KV label="Created">{fmtDateTime(offer.createdAt)}</KV>
            {offer.vestAt && (
              <KV label="Vests at">{fmtDateTime(offer.vestAt)}</KV>
            )}
            {offer.vestedAt && (
              <KV label="Vested at">{fmtDateTime(offer.vestedAt)}</KV>
            )}
            {offer.declinedAt && (
              <KV label="Declined at">{fmtDateTime(offer.declinedAt)}</KV>
            )}
          </div>
        </Card>

        {/* Eligibility snapshot — why was this user offered to? */}
        {Object.keys(snapshot).length > 0 && (
          <Card className="mb-4 bg-nurev-surface">
            <SectionLabel>Eligibility snapshot at offer time</SectionLabel>
            <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-nurev-text-soft">
              {JSON.stringify(snapshot, null, 2)}
            </pre>
          </Card>
        )}

        {/* Internal notes — audit trail across cron runs + admin actions */}
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Internal notes (audit trail)
            </label>
            <textarea
              value={internalNotes}
              onChange={e => setInternalNotes(e.target.value)}
              rows={4}
              className="w-full resize-none rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm font-mono text-[11px] text-nurev-text placeholder:text-nurev-muted-dim focus:border-amber-500/50 focus:outline-none"
              placeholder="Cron + admin actions log here"
            />
          </div>

          {error && <p className="text-xs text-red-300">{error}</p>}

          {/* Force-status overrides for ops. Audit-logged via the
              internalNotes append on the controller. */}
          {!isTerminal && (
            <div className="flex flex-wrap gap-2 pt-1">
              <ActionButton
                variant="positive"
                size="sm"
                disabled={update.isPending}
                onClick={() => handleForceStatus('vested')}
              >
                Force vested
              </ActionButton>
              <ActionButton
                variant="neutral"
                size="sm"
                disabled={update.isPending}
                onClick={() => handleForceStatus('expired')}
              >
                Force expired
              </ActionButton>
              <ActionButton
                variant="destructive"
                size="sm"
                disabled={update.isPending}
                onClick={() => handleForceStatus('declined')}
              >
                Force declined
              </ActionButton>
            </div>
          )}
          {isTerminal && (
            <p className="pt-1 text-[11px] text-nurev-muted">
              Terminal status. Status cannot be transitioned out of{' '}
              {STATUS_LABEL[offer.status]} from this UI.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-nurev-muted">{label}:</span>{' '}
      <span className="text-nurev-text">{children}</span>
    </div>
  );
}

function extractErr(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: string } } })?.response?.data
      ?.error || 'Update failed.'
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

export function RetentionScreen() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<RetentionStatus | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      page,
      limit: 20,
      sort: '-createdAt',
      status: statusFilter
    }),
    [page, statusFilter]
  );
  const { data, isLoading } = useRetention(params);
  const items = data?.docs || [];
  const totalPages = data?.totalPages || 1;
  const stats = useRetentionStats();
  const totalBonusVested = stats.data?.totalBonusVested || 0;

  const resetPage = () => setPage(1);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Retention
          </h1>
          <p className="mt-1 text-sm text-nurev-muted">
            Winback offers from the retention-bonus mechanic. Mac/Linux
            silent-uninstall catchers (heartbeat-silence cron) +
            future Windows MSI uninstall intercepts land here.
          </p>
        </div>
        {totalBonusVested > 0 && (
          <div className="rounded-lg border border-nurev-border bg-nurev-surface px-4 py-2 text-right">
            <div className="text-[10px] uppercase text-nurev-muted">
              Total bonus vested (points)
            </div>
            <div className="text-lg font-bold text-emerald-300">
              {totalBonusVested.toLocaleString()}
            </div>
          </div>
        )}
      </header>

      <StatsOverview />

      <Card className="!p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px]">
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Status
            </label>
            <Select<RetentionStatus | ''>
              value={statusFilter}
              onChange={v => {
                setStatusFilter(v);
                resetPage();
              }}
              options={STATUS_OPTIONS.map(o => ({
                value: o.value,
                label: o.label
              }))}
            />
          </div>
        </div>
      </Card>

      <Card className="!p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-nurev-border text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-nurev-muted">
              <tr>
                <th className="px-3 py-2 text-left">Person</th>
                <th className="px-3 py-2 text-left">Publisher</th>
                <th className="px-3 py-2 text-left">App</th>
                <th className="px-3 py-2 text-right">Bonus</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Vest at</th>
                <th className="px-3 py-2 text-left">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-nurev-border">
              {isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6">
                    <Skeleton className="h-5 w-full" />
                  </td>
                </tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-8 text-center text-sm text-nurev-muted"
                  >
                    No retention offers yet for the current filters.
                  </td>
                </tr>
              )}
              {items.map(o => {
                const publisher: PublisherRef | null =
                  typeof o.publisherId === 'object' && o.publisherId !== null
                    ? (o.publisherId as PublisherRef)
                    : null;
                const app: AppRef | null =
                  typeof o.appId === 'object' && o.appId !== null
                    ? (o.appId as AppRef)
                    : null;
                return (
                  <tr
                    key={o._id}
                    onClick={() => setSelectedId(o._id)}
                    className="cursor-pointer hover:bg-white/[0.03]"
                  >
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-nurev-muted">
                      {o.personId.slice(-8)}
                    </td>
                    <td className="px-3 py-2 text-nurev-text-soft">
                      {publisher?.companyName || publisher?.name || '—'}
                    </td>
                    <td className="px-3 py-2 text-nurev-text-soft">
                      {app?.name || app?.appKey || '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-nurev-text">
                      {o.bonusAmount.toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <Badge value={STATUS_LABEL[o.status]} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-nurev-muted">
                      {o.vestAt ? fmtDate(o.vestAt) : '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-nurev-muted">
                      {fmtDate(o.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}

      {selectedId && (
        <DetailModal
          offerId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
